import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./NavbarStyle.css";

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Hide navbar on dashboard routes when user is logged in
  const isDashboardRoute = location.pathname.startsWith("/dashboard");
  if (isDashboardRoute && user) {
    return null;
  }

  return (
    <nav className="navbar navbar-expand-lg tpdf-navbar-modern">
      <div className="container px-3 px-lg-4">
        {/* Brand Logo */}
        <Link
          className="navbar-brand tpdf-brand-modern d-flex align-items-center"
          to="/"
        >
          <div className="tpdf-brand-icon-modern me-3">
            <i className="fa fa-file-pdf-o"></i>
          </div>
          <div className="d-flex flex-column">
            <span className="tpdf-brand-name-modern fw-bold">
              Tinypdfconverter
            </span>
            {/* <span className="tpdf-brand-subtitle">Converter</span> */}
          </div>
        </Link>

        {/* Mobile Toggle Button */}
        <button
          className="navbar-toggler tpdf-toggler-modern"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarContent"
          aria-controls="navbarContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="tpdf-toggler-bar"></span>
          <span className="tpdf-toggler-bar"></span>
          <span className="tpdf-toggler-bar"></span>
        </button>

        {/* Navigation Menu */}
        <div className="collapse navbar-collapse" id="navbarContent">
          <ul className="navbar-nav ms-auto align-items-lg-center gap-2">
            {/* Convert Files Link */}
            <li className="nav-item">
              <Link
                className={`nav-link tpdf-nav-link-modern px-3 py-2 rounded-pill d-flex align-items-center ${
                  isActive("/file-converter") ? "active" : ""
                }`}
                to="/file-converter"
              >
                <i className="fa fa-refresh me-2"></i>
                <span>Convert</span>
              </Link>
            </li>

            {user ? (
              <>
                {/* Files Link */}
                <li className="nav-item">
                  <Link
                    className={`nav-link tpdf-nav-link-modern px-3 py-2 rounded-pill d-flex align-items-center ${
                      isActive("/files") ? "active" : ""
                    }`}
                    to="/files"
                  >
                    <i className="fa fa-cloud me-2"></i>
                    <span>My Files</span>
                  </Link>
                </li>

                {/* Usage Link */}
                <li className="nav-item">
                  <Link
                    className={`nav-link tpdf-nav-link-modern px-3 py-2 rounded-pill d-flex align-items-center ${
                      isActive("/usage") ? "active" : ""
                    }`}
                    to="/usage"
                  >
                    <i className="fa fa-pie-chart me-2"></i>
                    <span>Usage</span>
                  </Link>
                </li>

                {/* Billing Link */}
                <li className="nav-item">
                  <Link
                    className={`nav-link tpdf-nav-link-modern px-3 py-2 rounded-pill d-flex align-items-center ${
                      isActive("/billing") ? "active" : ""
                    }`}
                    to="/billing"
                  >
                    <i className="fa fa-credit-card-alt me-2"></i>
                    <span>Billing</span>
                  </Link>
                </li>

                {/* Admin Link */}
                {user.role === "admin" && (
                  <li className="nav-item">
                    <Link
                      className={`nav-link tpdf-nav-link-admin px-3 py-2 rounded-pill d-flex align-items-center ${
                        isActive("/admin/users") ? "active" : ""
                      }`}
                      to="/admin/users"
                    >
                      <i className="fa fa-shield me-2"></i>
                      <span>Admin</span>
                    </Link>
                  </li>
                )}

                {/* User Actions Container */}
                <li className="nav-item ms-lg-3">
                  <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-3">
                    {/* Credits Badge */}
                    <div className="tpdf-credits-modern d-flex align-items-center gap-2 px-3 py-2">
                      <div className="tpdf-credits-icon">
                        <i className="fa fa-bolt"></i>
                      </div>
                      <div className="d-flex flex-column">
                        <span className="tpdf-credits-count fw-bold">
                          {user.subscription?.extractionCredits || 0}
                        </span>
                        <span className="tpdf-credits-label">Credits</span>
                      </div>
                    </div>

                    {/* User Profile & Logout */}
                    <div className="dropdown">
                      <button
                        className="btn tpdf-user-dropdown d-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <div className="tpdf-user-avatar">
                          <i className="fa fa-user"></i>
                        </div>
                        <span className="tpdf-user-name">
                          {user.name || user.email}
                        </span>
                        <i className="fa fa-chevron-down small"></i>
                      </button>
                      <ul className="dropdown-menu dropdown-menu-end tpdf-dropdown-modern">
                        <li>
                          <button
                            className="dropdown-item tpdf-dropdown-item d-flex align-items-center gap-2"
                            onClick={handleLogout}
                          >
                            <i className="fa fa-sign-out"></i>
                            <span>Logout</span>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </>
            ) : (
              <>
                {/* Sign In Link */}
                <li className="nav-item">
                  <Link
                    className={`nav-link tpdf-nav-link-modern px-3 py-2 rounded-pill d-flex align-items-center ${
                      isActive("/login") ? "active" : ""
                    }`}
                    to="/login"
                  >
                    <i className="fa fa-user me-2"></i>
                    <span>Sign In</span>
                  </Link>
                </li>

                {/* Get Started Button */}
                <li className="nav-item ms-lg-2">
                  <Link
                    className="btn tpdf-cta-modern d-flex align-items-center justify-content-center gap-2 px-4 py-2 rounded-pill"
                    to="/register"
                  >
                    <i className="fa fa-rocket me-1"></i>
                    <span>Get Started Free</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
